import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/PricesService';
import { useState } from 'react';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const usePrices = (productId?: string) => {
    const queryClient = useQueryClient();
    const [limit] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [selection] = useState<Record<string, boolean>>({});

    const sort = { column: 'created', direction: 'desc' as const };

    // INFINITE QUERY
    const pricesQuery = useInfiniteQuery({
        queryKey: ['prices', productId, keyword, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords(productId!, { limit, sort, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        enabled: !!productId
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Price deleted');
            queryClient.invalidateQueries({ queryKey: ['prices', productId] });
        },
        onError: notifyError
    });

    if (!productId) {
        return {
            items: [],
            visibleItems: [],
            loading: false,
            visibleCount: 0,
            totalCount: 0,
            nextToken: null,
            fetchRecords: () => {},
            deleteRecords: () => {},
            handleFilterByKeyword: () => {},
            deleteMutation: { mutate: () => {} }
        };
    }

    const fetchRecordsAction = () => {
        if (pricesQuery.hasNextPage) {
            pricesQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = pricesQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: pricesQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: pricesQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};
